/**
 * Conversations API hooks — TanStack Query layer over /api/conversations.
 *
 * Admin/Manager document/edit conversations; employees see only their own.
 * Separation of duties: cannot document/edit a conversation about self.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { DocumentedConversation, Paginated } from '@shared/types';
import {
  conversationCreateInput,
  conversationUpdateInput,
} from '@shared/schemas/index.js';
import { z } from 'zod';

export type ConversationCreateBody = z.input<typeof conversationCreateInput>;
export type ConversationUpdateBody = z.input<typeof conversationUpdateInput>;

export const conversationKeys = {
  all: ['conversations'] as const,
  list: (params?: { employeeId?: string }) =>
    [...conversationKeys.all, 'list', params ?? {}] as const,
};

/** List documented conversations, optionally scoped to an employee. */
export function useConversations(params?: { employeeId?: string }) {
  return useQuery({
    queryKey: conversationKeys.list(params),
    queryFn: () =>
      api.get<Paginated<DocumentedConversation>>(`/api/conversations${qs(params)}`),
  });
}

/** Create a documented conversation (Admin/Manager; not about self). */
export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ConversationCreateBody) =>
      api.post<DocumentedConversation>('/api/conversations', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
  });
}

/** Update a documented conversation (Admin/Manager; not about self). */
export function useUpdateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ConversationUpdateBody }) =>
      api.put<DocumentedConversation>(`/api/conversations/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
  });
}

/** Delete a documented conversation (Admin/Manager; not about self). */
export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/api/conversations/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
  });
}
